import {
  CandidateFitPagedResult,
  CreateOpportunityRequest,
  OpportunityDetail,
  OpportunityListItem,
  PositionRequest,
  ShortlistEntry,
  UpdateOpportunityRequest,
  UpsertShortlistEntryRequest,
} from '../models/opportunity.model';
import { BackendHttpService } from './backendHttpService';

export class OpportunityService extends BackendHttpService {
  constructor() {
    super('api/opportunity');
  }

  getAll(): Promise<OpportunityListItem[]> {
    return this.get<OpportunityListItem[]>('');
  }

  getById(id: number): Promise<OpportunityDetail> {
    return this.get<OpportunityDetail>(`/${id}`);
  }

  create(data: CreateOpportunityRequest): Promise<OpportunityDetail> {
    return this.post<CreateOpportunityRequest, OpportunityDetail>(data, '');
  }

  update(id: number, data: UpdateOpportunityRequest): Promise<OpportunityDetail> {
    return this.put<UpdateOpportunityRequest, OpportunityDetail>(data, `/${id}`);
  }

  archive(id: number): Promise<void> {
    return this.delete(`/${id}`);
  }

  // ── Positions ───────────────────────────────────────────────────────────────

  addPosition(opportunityId: number, data: PositionRequest): Promise<OpportunityDetail> {
    return this.post<PositionRequest, OpportunityDetail>(data, `/${opportunityId}/positions`);
  }

  updatePosition(opportunityId: number, positionId: number, data: PositionRequest): Promise<OpportunityDetail> {
    return this.put<PositionRequest, OpportunityDetail>(data, `/${opportunityId}/positions/${positionId}`);
  }

  removePosition(opportunityId: number, positionId: number): Promise<void> {
    return this.delete(`/${opportunityId}/positions/${positionId}`);
  }

  // ── Shortlist ───────────────────────────────────────────────────────────────

  getShortlist(opportunityId: number, positionId?: number): Promise<ShortlistEntry[]> {
    const params = positionId != null ? `?positionId=${positionId}` : '';
    return this.get<ShortlistEntry[]>(`/${opportunityId}/shortlist${params}`);
  }

  upsertShortlistEntry(opportunityId: number, positionId: number, data: UpsertShortlistEntryRequest): Promise<ShortlistEntry> {
    return this.put<UpsertShortlistEntryRequest, ShortlistEntry>(data, `/${opportunityId}/shortlist/${positionId}`);
  }

  removeShortlistEntry(opportunityId: number, positionId: number, personId: number): Promise<void> {
    return this.delete(`/${opportunityId}/shortlist/${positionId}/${personId}`);
  }

  // ── Candidate Fit Scoring ───────────────────────────────────────────────────

  getCandidates(
    opportunityId: number,
    positionId: number,
    params?: { page?: number; pageSize?: number; minFitScore?: number; includeRejected?: boolean },
  ): Promise<CandidateFitPagedResult> {
    const qs = new URLSearchParams();
    if (params?.page != null) qs.set('page', String(params.page));
    if (params?.pageSize != null) qs.set('pageSize', String(params.pageSize));
    if (params?.minFitScore != null) qs.set('minFitScore', String(params.minFitScore));
    if (params?.includeRejected != null) qs.set('includeRejected', String(params.includeRejected));
    const suffix = qs.size > 0 ? `?${qs}` : '';
    return this.get<CandidateFitPagedResult>(`/${opportunityId}/positions/${positionId}/candidates${suffix}`);
  }
}

export const opportunityService = new OpportunityService();
